/**
 * Venttra - FireCMS Control Plane
 * Headless CMS for managing destinations and content workflow.
 */
import React, { useCallback, useMemo } from "react";
import {
  AppBar,
  Authenticator,
  CircularProgressCenter,
  Drawer,
  FireCMS,
  FireCMSi18nProvider,
  ModeControllerProvider,
  NavigationRoutes,
  Scaffold,
  SideDialogs,
  SnackbarProvider,
  useBuildLocalConfigurationPersistence,
  useBuildModeController,
  useBuildNavigationController,
  useValidateAuthenticator,
} from "@firecms/core";
import {
  FirebaseAuthController,
  FirebaseLoginView,
  FirebaseSignInProvider,
  FirebaseUserWrapper,
  useFirebaseAuthController,
  useFirebaseStorageSource,
  useFirestoreDelegate,
  useInitialiseFirebase,
} from "@firecms/firebase";
import { CenteredView } from "@firecms/ui";
import { cmsCollections } from "./cms_config";
import { firebaseConfig } from "./firebase_config";
import { DataToolsView } from "./views/DataToolsView";
import { GlobalPublishAction } from "./components/GlobalPublishAction";
import { fullscreenClosePlugin } from "./components/FullscreenClosePlugin";

/** Path to Venttra CMS logo (static asset) */
const CMS_LOGO = "/venttra_cms_logo.svg";

/** Email whitelist for CMS access (comma-separated, from VITE_CMS_ALLOWED_EMAILS) */
const CMS_EMAIL_WHITELIST = new Set(
  (import.meta.env.VITE_CMS_ALLOWED_EMAILS || "")
    .split(",")
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
);

function App() {
  const myAuthenticator: Authenticator<FirebaseUserWrapper> = useCallback(
    async ({ user }) => {
      if (!user?.email) return false;
      return CMS_EMAIL_WHITELIST.has(user.email.toLowerCase());
    },
    []
  );

  const collections = useMemo(() => cmsCollections, []);

  const {
    firebaseApp,
    firebaseConfigLoading,
    configError,
  } = useInitialiseFirebase({ firebaseConfig });

  const modeController = useBuildModeController();
  const signInOptions: FirebaseSignInProvider[] = ["google.com", "password"];

  const authController: FirebaseAuthController = useFirebaseAuthController({
    firebaseApp,
    signInOptions,
    defineRolesFor: async (user) => {
      const email = (user as { email?: string }).email?.toLowerCase();
      if (!email || !CMS_EMAIL_WHITELIST.has(email)) return undefined;
      const tokenResult = await (user as { getIdTokenResult?: () => Promise<{ claims?: Record<string, unknown> }> }).getIdTokenResult?.();
      const claims = tokenResult?.claims;
      const role = claims?.role as string | undefined;
      const roles = claims?.roles as string[] | undefined;
      if (role === "admin" || roles?.includes("admin"))
        return [{ id: "admin", name: "Admin", isAdmin: true }];
      return [{ id: "editor", name: "Editor" }];
    },
  });

  const userConfigPersistence = useBuildLocalConfigurationPersistence();
  const firestoreDelegate = useFirestoreDelegate({ firebaseApp });
  const storageSource = useFirebaseStorageSource({ firebaseApp });

  const {
    authLoading,
    canAccessMainView,
    notAllowedError,
  } = useValidateAuthenticator({
    authController,
    authenticator: myAuthenticator,
    dataSourceDelegate: firestoreDelegate,
    storageSource,
  });

  const dataToolsView = useMemo(
    () => ({
      path: "data-tools",
      name: "Data Tools",
      description: "Destinations, guides, batch ops, hierarchy",
      group: "Content",
      view: <DataToolsView />,
    }),
    []
  );

  const navigationController = useBuildNavigationController({
    disabled: authLoading,
    collections,
    views: ({ user }) => (user ? [dataToolsView] : []),
    plugins: [fullscreenClosePlugin],
    authController,
    dataSourceDelegate: firestoreDelegate,
  });

  if (firebaseConfigLoading || !firebaseApp) {
    return <CircularProgressCenter />;
  }

  if (configError) {
    return <CenteredView>{configError}</CenteredView>;
  }

  return (
    <FireCMSi18nProvider
      locale="en"
      translations={{
        en: {
          // Explicit spaces so uppercase button styling never shows underscores.
          save_and_close: "Save and close",
          create_and_close: "Create and close",
          create_copy: "Create copy",
          create_copy_and_close: "Create copy and close",
          add: "Add",
          add_specific: "Add {{name}}",
        },
      }}
    >
    <SnackbarProvider>
      <ModeControllerProvider value={modeController}>
        <FireCMS
          navigationController={navigationController}
          authController={authController}
          userConfigPersistence={userConfigPersistence}
          dataSourceDelegate={firestoreDelegate}
          storageSource={storageSource}
        >
          {({ loading }) => {
            if (loading || authLoading) {
              return <CircularProgressCenter size="large" />;
            }

            if (!canAccessMainView) {
              return (
                <FirebaseLoginView
                  authController={authController}
                  firebaseApp={firebaseApp}
                  signInOptions={signInOptions}
                  notAllowedError={notAllowedError}
                  logo={CMS_LOGO}
                  className="venttra-login-view"
                />
              );
            }

            return (
              <Scaffold autoOpenDrawer={false} logo={CMS_LOGO}>
                <AppBar
                  title="Venttra CMS"
                  logo={CMS_LOGO}
                  endAdornment={<GlobalPublishAction />}
                />
                <Drawer />
                <NavigationRoutes />
                <SideDialogs />
              </Scaffold>
            );
          }}
        </FireCMS>
      </ModeControllerProvider>
    </SnackbarProvider>
    </FireCMSi18nProvider>
  );
}

export default App;
